/**
 * 画面に出すエラー文を 1 か所で作る（2026-10-03・UX 監査 X-2）。
 *
 * 以前は画面ごとに `e?.response?.data?.message || e?.message || '…'` と書いていて、
 * 「Request failed with status code 500」「Network Error」「timeout of 30000ms exceeded」「Not Found」のような
 * 英語のエラー文がそのまま出ていた。
 *
 * 優先順: サーバの日本語の文言 → 入力の誤り（Laravel の errors の最初の 1 つ・日本語のとき）→ 状態ごとの日本語の決まり文句。
 * 英語だけの文言は出さない。
 */

/** 日本語（ひらがな・カタカナ・漢字）を含むか */
export const hasJapanese = (s: unknown): boolean => typeof s === 'string' && /[ぁ-んァ-ヶ一-龠々ー]/.test(s);

const TIMEOUT_MESSAGE = '通信に時間がかかっています。電波の良い場所でもう一度お試しください（処理が済んでいることもあるので、画面を読み直して確かめてください）';
const NETWORK_MESSAGE = '通信できませんでした。電波やネットの接続を確かめて、もう一度お試しください';
const GENERIC_MESSAGE = 'うまくいきませんでした。もう一度お試しください（続くときは画面を読み直してください）';
/**
 * 🔴 送り先のルートが無い（= アプリの不具合）。2026-10-12 に本番で踏んだ。
 *
 * FE の `API_ENDPOINTS.profile.updatePassword` が実在しないパスを指していて、
 * **パスワード変更が全画面で 404** だった。Laravel は
 * `The route api/... could not be found.` を返すが、**英語なので上の仕組みが
 * GENERIC_MESSAGE に潰してしまい**、「もう一度お試しください」としか出なかった
 * （「もう一度」では絶対に直らない種類の失敗なのに）。
 * ⚠️ `/not found/i` では拾えない（"could not **be** found"）。
 * → 配線の間違いだと分かる文言にして、同じ隠れ方をしないようにする。
 */
const ROUTE_MISSING_MESSAGE = 'この操作の送り先が見つかりませんでした。アプリの不具合の可能性があるので、サポート（support@logmeapp.com）へご連絡ください';

/** Laravel の「ルートが無い」応答か（英語の文言でしか判別できない） */
export const isRouteMissing = (text: unknown): boolean =>
  typeof text === 'string' && /the route .* could not be found/i.test(text);

/** HTTP の状態ごとの日本語の決まり文句 */
export function statusMessage(status: number | undefined): string {
  if (!status) return GENERIC_MESSAGE;
  if (status === 400 || status === 422) return '入力を確かめてください';
  if (status === 401) return 'ログインの有効期限が切れました。もう一度ログインしてください';
  if (status === 403) return 'この操作をする権限がありません';
  if (status === 404) return '対象が見つかりませんでした（ほかの端末で消された可能性があります）。画面を読み直してください';
  if (status === 409) return 'ほかの端末で先に変更されたか、今はできない操作です。画面を読み直してからもう一度お試しください';
  if (status === 419) return '画面を開いてから時間がたちました。画面を読み直してください';
  if (status === 429) return '操作が続きました。少し待ってからもう一度お試しください';
  if (status >= 500) return 'サーバでエラーが起きました。少し待ってからもう一度お試しください（続くときはサポートへ）';
  return GENERIC_MESSAGE;
}

/**
 * 英語だけのエラー文を、日本語の決まり文句に置き換える（日本語を含む文はそのまま）。
 * 呼び出し側が `e.message` をそのまま渡している箇所の受け皿（useAppToast が使う）。
 */
export function localizeErrorText(text: unknown): string | undefined {
  if (text == null || text === '') return undefined;
  if (typeof text !== 'string') return undefined;
  if (hasJapanese(text)) return text;
  // 英大文字のエラーコード（NOT_PENDING・VISIT_CLOSED 等）は原因の手がかりなので残す（決まり文句にすると「もう一度」と誤って案内する）
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(text.trim())) return `エラーコード: ${text.trim()}`;
  // 🔴 **いちばん先に見る**。ルートのパスが文言に入るので、`network-printer` のような
  //    パスだと下の `/network/i` に奪われ「通信できませんでした。電波やネットの接続を…」になる。
  //    配線の間違いなのに**店の Wi-Fi のせいにして、直らない再試行を案内する**ことになる。
  if (isRouteMissing(text)) return ROUTE_MISSING_MESSAGE;
  const m = text.match(/status code (\d{3})/i);
  if (m) return statusMessage(Number(m[1]));
  if (/timeout|ECONNABORTED/i.test(text)) return TIMEOUT_MESSAGE;
  if (/network/i.test(text)) return NETWORK_MESSAGE;
  if (/not found/i.test(text)) return statusMessage(404);
  if (/unauthenticated|unauthorized/i.test(text)) return statusMessage(401);
  if (/forbidden|access denied|this action is unauthorized/i.test(text)) return statusMessage(403);
  return GENERIC_MESSAGE;
}

/** axios などのエラーから、画面に出すエラー文を作る */
export function errorMessage(e: unknown, fallback?: string): string {
  const err = e as any;
  const data = err?.response?.data;
  // 0) 送り先のルートが無い（配線の間違い）。status だけだと「消された」と誤案内するので先に見る
  if (isRouteMissing(data?.message)) return ROUTE_MISSING_MESSAGE;
  // 1) サーバの日本語の文言
  if (hasJapanese(data?.message)) return data.message;
  // 2) 入力の誤り（Laravel の errors: { field: [message] }）
  const errors = data?.errors;
  if (errors && typeof errors === 'object') {
    for (const v of Object.values(errors)) {
      const first = Array.isArray(v) ? v[0] : v;
      if (hasJapanese(first)) return String(first);
    }
  }
  // 3) 通信できなかった（応答が無い）
  if (err && !err.response && (err.code === 'ECONNABORTED' || /timeout/i.test(String(err.message ?? '')))) return TIMEOUT_MESSAGE;
  if (err && !err.response && (err.request || /network/i.test(String(err.message ?? '')))) return NETWORK_MESSAGE;
  // 4) 状態ごとの決まり文句（呼び出し側の文言があればそちらを優先）
  if (fallback && hasJapanese(fallback)) {
    const status = err?.response?.status;
    return status ? `${fallback}（${statusMessage(status)}）` : fallback;
  }
  if (err?.response?.status) return statusMessage(err.response.status);
  if (hasJapanese(err?.message)) return err.message;
  return GENERIC_MESSAGE;
}
